import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, CardHeader, CardContent, CircularProgress, Alert, IconButton, Switch, FormControlLabel } from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen'; // Diagonal arrows pointing outward
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'; // Arrows pointing inward
import { useDashboardStyles } from './dashboardStyles';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Debug Logging
const DEBUG_MODE = false;
const log = (...args) => {
  if (DEBUG_MODE) {
    console.log('[SkipRate Component]', ...args);
  }
};

const skipRateLineColor = 'rgba(149, 117, 205, 0.8)';
const headerColorFromMockup = '#0087B6';

// Color constants for skip rate bands with transparency
const chartBandGreen = 'rgba(217, 234, 218, 0.6)';  // Light green for low skip rates
const chartBandBlue = 'rgba(226, 239, 180, 0.6)';   // Light blue for medium skip rates
const chartBandYellow = 'rgba(233, 188, 145, 0.6)'; // Light yellow/orange for high skip rates

// Text colors for legend
const textColorGreen = '#2ecc71';  // Green text color
const textColorBlue = '#3498db';   // Blue text color 
const textColorYellow = '#f39c12'; // Yellow/Orange text color

// Threshold values - skip rates are stored as decimals (0.15 = 15%)
// Based on the image: <15 Green, 15-39 Blue, >39 Yellow
const thresholdGreenUpperLimit = 0.15;  // Green: 0% to <15%
const thresholdBlueLowerLimit = 0.15;   // Blue: 15% to 39%
const thresholdBlueUpperLimit = 0.39;   // Blue upper boundary
const thresholdYellowLowerLimit = 0.39; // Yellow: >39%

const SkipRate = ({ data, loading, error, books = [], student = {}, isExpanded = false, isCollapsed = false, onExpand }) => {
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const classes = useDashboardStyles();
  
  // State for level filtering - default to true (on)
  const [showLeveledData, setShowLeveledData] = useState(true);
  
  // Helper function to get book title by ID
  const getBookTitle = (bookId) => {
    if (!bookId) return 'Unknown Book';
    
    const book = books.find(b => b.id === bookId || b.id === String(bookId));
    if (!book || !book.name) return `Book ID: ${bookId}`;
    
    // Construct title with level from book object
    let title = book.name;
    if (book.bookLevel) {
      title = `${title} (${book.bookLevel})`;
    }
    
    // Truncate title if too long (over 50 characters to account for level)
    return title.length > 50 ? `${title.substring(0, 47)}...` : title;
  };

  // Format date to MM/DD format
  const formatDate = (dateString) => {
    try {
      // Handle the date format "2025-05-15-04:00"
      // First replace the last hyphen with a space to make it ISO compatible
      const formattedDateString = dateString.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
      const date = new Date(formattedDateString);
      
      if (isNaN(date.getTime())) {
        log('Invalid date:', dateString);
        return 'Invalid date';
      }
      
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } catch (e) {
      log('Error formatting date:', e, dateString);
      return 'Invalid date';
    }
  };

  // Filter data based on level setting
  const filterDataByLevel = (data) => {
    // Only filter if dashboard is enabled, showLeveledData is true, and we have a current level
    if (student.isDashboardEnabled === 'Y' && showLeveledData && student.currentBuddyBooksLevel) {
      return data.filter(item => {
        if (!item.bookId) return false;
        const book = books.find(b => b.id === item.bookId || b.id === String(item.bookId));
        return book && book.bookLevel === student.currentBuddyBooksLevel;
      });
    }
    return data;
  };

  // Process skip rate data for the chart
  const processSkipRateData = (skipRateData) => {
    if (!skipRateData || skipRateData.length === 0) {
      log('No skip rate data available');
      return { labels: [], values: [], original: [] };
    }
    
    // Filter out any malformed data entries
    let validData = skipRateData.filter(item => {
      // Check if we have both skipRate and date as expected
      return (
        typeof item === 'object' && 
        item !== null && 
        'skipRate' in item && 
        'date' in item &&
        typeof item.skipRate === 'number' &&
        !isNaN(item.skipRate)
      );
    });
    
    // Apply level filtering if enabled
    validData = filterDataByLevel(validData);
    
    if (validData.length === 0) {
      log('No valid data entries after filtering');
      return { labels: [], values: [], original: [] };
    }
    
    log('Valid data entries:', validData.length);
    
    // Group by date and calculate weighted averages
    const groupedByDate = {};
    
    validData.forEach(item => {
      const date = item.date;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          date,
          items: [],
          totalWeight: 0,
          weightedSum: 0
        };
      }
      
      const entryCount = item.entryCount || 1; // Default to 1 if not provided
      const skipRate = item.skipRate;
      
      groupedByDate[date].items.push(item);
      groupedByDate[date].totalWeight += entryCount;
      groupedByDate[date].weightedSum += skipRate * entryCount;
    });
    
    // Calculate weighted averages and create aggregated data
    const aggregatedData = Object.values(groupedByDate).map(group => ({
      date: group.date,
      skipRate: group.weightedSum / group.totalWeight,
      originalItems: group.items,
      entryCount: group.totalWeight
    }));
    
    // Sort data by date - improve date parsing for consistency
    const sortedData = [...aggregatedData].sort((a, b) => {
      try {
        // Convert date strings to standardized format for comparison
        const dateA = a.date.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
        const dateB = b.date.replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2})/, '$1T$2');
        
        return new Date(dateA) - new Date(dateB);
      } catch (e) {
        log('Error sorting dates:', e);
        return 0;
      }
    });

    // Format dates for display (MM/DD format)
    const labels = sortedData.map(item => formatDate(item.date));

    // Extract skip rate values
    const values = sortedData.map(item => item.skipRate);

    log('Processed skip rate data', { labels, values });
    return { labels, values, original: sortedData };
  };

  // Calculate average skip rate
  const getAverageSkipRate = () => {
    if (!data || data.length === 0) return null;
    
    // Filter valid entries
    const validData = data.filter(item => 
      typeof item === 'object' && 
      item !== null && 
      'skipRate' in item &&
      typeof item.skipRate === 'number' &&
      !isNaN(item.skipRate)
    );
    
    if (validData.length === 0) return null;
    
    const total = validData.reduce((sum, item) => sum + item.skipRate, 0);
    return (total / validData.length * 100).toFixed(1) + '%';
  };

  // Destroy chart when component unmounts or data changes
  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        log('Destroying chart instance');
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  // Prepare chart data
  const chartData = processSkipRateData(data);
  log('Chart data ^^^^^', chartData);
  
  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false,
    animation: {
      duration: 500
    },
    layout: {
      padding: {
        top: 20,
        bottom: 15,
        left: 10,
        right: 10
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 1,
        ticks: {
          callback: function(value) {
            return (value * 100).toFixed(0) + '%';
          }
        },
        grid: {
          color: 'rgba(200, 200, 200, 0.3)'
        }
      },
      x: {
        grid: {
          color: 'rgba(200, 200, 200, 0.3)'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: isExpanded ? 15 : 5,
          padding: 5,
          font: {
            size: 11
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        position: 'nearest',
        mode: 'point',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        padding: 10,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const dataIndex = context.dataIndex;
            const originalData = chartData.original[dataIndex];
            const originalItems = originalData && originalData.originalItems || [];
            
            if (originalItems.length === 1) {
              // Single datapoint
              const item = originalItems[0];
              const bookId = item.bookId || 'N/A';
              const skipRate = item.skipRate;
              
              let displaySkipRate;
              if (skipRate >= 1.0) {
                displaySkipRate = "100%";
              } else {
                displaySkipRate = (skipRate * 100).toFixed(1) + '%';
              }
              
              return [
                `Skip Rate: ${displaySkipRate}`,
                getBookTitle(bookId)
              ];
            } else {
              // Multiple datapoints - show weighted average and original items
              const aggregatedSkipRate = originalData.skipRate;
              let displayAggregated;
              if (aggregatedSkipRate >= 1.0) {
                displayAggregated = "100%";
              } else {
                displayAggregated = (aggregatedSkipRate * 100).toFixed(1) + '%';
              }
              
              const result = [`Weighted Avg: ${displayAggregated}`];
              
              // Add each original datapoint
              originalItems.forEach((item, index) => {
                const bookId = item.bookId || 'N/A';
                const skipRate = item.skipRate;
                const entryCount = item.entryCount || 1;
                
                let displaySkipRate;
                if (skipRate >= 1.0) {
                  displaySkipRate = "100%";
                } else {
                  displaySkipRate = (skipRate * 100).toFixed(1) + '%';
                }
                
                result.push(`${displaySkipRate} (${entryCount}x) - ${getBookTitle(bookId)}`);
              });
              
              return result;
            }
          },
          title: function(context) {
            const dataIndex = context[0].dataIndex;
            const originalData = chartData.original[dataIndex];
            if (originalData && originalData.date) {
              // Convert the date string to a formatted date
              const rawDate = originalData.date;
              return formatDate(rawDate);
            }
            return 'Unknown date';
          }
        }
      },
      expandedStatePlugin: {
        isExpanded: isExpanded
      }
    }
  };

  // Chart data in the format expected by react-chartjs-2
  const chartConfig = {
    labels: chartData.labels,
    datasets: [
      // Skip rate data line - should be on top
      {
        label: 'Skip Rate',
        data: chartData.values,
        borderColor: skipRateLineColor,
        backgroundColor: 'transparent', // No background fill
        borderWidth: 2,
        fill: false, // Disable fill
        tension: 0.3,
        pointRadius: 4,
        pointBorderWidth: 1,
        pointBackgroundColor: 'white',
        pointBorderColor: skipRateLineColor,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: skipRateLineColor,
        pointHoverBorderColor: 'white',
        pointHoverBorderWidth: 2
      }
    ]
  };

  // Render different content based on state
  const renderContent = () => {
    if (loading) {
      log('Rendering loading state');
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          minHeight: '200px'
        }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      log('Rendering error state', error);
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error" variant="body2">
            Failed to load skip rate data: {error}
          </Typography>
        </Box>
      );
    }

    if (!data || data.length === 0 || chartData.labels.length === 0) {
      log('Rendering empty state');
      return (
        <Typography 
          variant="body1" 
          align="center" 
          sx={{ marginTop: '80px', color: 'text.secondary' }}
        >
          No skip rate data available
        </Typography>
      );
    }

    log('Rendering chart with', chartData.labels.length, 'data points');
    
    // Create chart options with expanded state property
    const chartOptions = { 
      ...options,
      plugins: {
        ...options.plugins,
        expandedStatePlugin: {
          isExpanded: isExpanded
        }
      }
    };
    
    // Define custom plugin for background color bands
    const plugins = [{
      id: 'customBackgroundBands',
      beforeDraw: (chart) => {
        // Read the expanded state from the chart options
        const chartIsExpanded = chart.options && 
                                chart.options.plugins && 
                                chart.options.plugins.expandedStatePlugin && 
                                chart.options.plugins.expandedStatePlugin.isExpanded || false;
        
        const {ctx, chartArea, scales} = chart;
        if (!chartArea) return;
        
        const yScale = scales.y;
        
        // Clear the entire chart area first
        ctx.fillStyle = 'white';
        ctx.fillRect(chartArea.left, chartArea.top, chartArea.width, chartArea.bottom - chartArea.top);
        
        // Only draw colored bands if expanded
        if (chartIsExpanded) {
          // Draw green band (0% to 15%)
          ctx.fillStyle = chartBandGreen;
          ctx.fillRect(
            chartArea.left, 
            yScale.getPixelForValue(thresholdGreenUpperLimit), // From 15%
            chartArea.width,
            yScale.getPixelForValue(0) - yScale.getPixelForValue(thresholdGreenUpperLimit) // To 0%
          );
          
          // Draw blue band (15% to 39%)
          ctx.fillStyle = chartBandBlue;
          ctx.fillRect(
            chartArea.left,
            yScale.getPixelForValue(thresholdBlueUpperLimit), // From 39%
            chartArea.width,
            yScale.getPixelForValue(thresholdBlueLowerLimit) - yScale.getPixelForValue(thresholdBlueUpperLimit) // From 15% to 39%
          );
          
          // Draw yellow band (39% to 100%)
          ctx.fillStyle = chartBandYellow;
          ctx.fillRect(
            chartArea.left,
            chartArea.top, // From top of chart
            chartArea.width,
            yScale.getPixelForValue(thresholdYellowLowerLimit) - chartArea.top // To 39%
          );
        }
      }
    }];
    
    return (
      <Box 
        ref={chartContainerRef}
        sx={{ 
          height: '270px',
          width: '100%', 
          position: 'relative',
          maxHeight: '270px',
          overflow: isCollapsed ? 'hidden' : 'visible',
          marginBottom: '10px',
          transition: 'all 0.5s ease'
        }}
        className="chart-container"
      >
        <Line 
          options={chartOptions} 
          data={chartConfig} 
          fallbackContent={<Typography>Chart could not be rendered</Typography>}
          plugins={plugins}
        />
      </Box>
    );
  };

  return (
    <Card 
      className={`${classes.card} ${isCollapsed ? classes.cardCollapsed : ''} skip-rate-card ${isExpanded ? 'expanded' : ''} ${isCollapsed ? 'collapsed' : ''}`}
    >
      <CardHeader 
        title="Skip Rate" 
        subheader={!loading && !error && data && data.length > 0 && !isCollapsed ? `Avg: ${getAverageSkipRate()}` : null}
        className={isCollapsed ? classes.cardHeaderCollapsed : classes.cardHeader}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Level filter toggle - only show if dashboard is enabled */}
            {student.isDashboardEnabled === 'Y' && !isCollapsed && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '8px' }}>
                <Typography variant="caption" style={{ fontSize: '0.75rem', marginTop: '5px', marginBottom: '2px', textAlign: 'center' }}>
                  {showLeveledData ? `Showing ${student.currentBuddyBooksLevel} Data` : 'Showing All Data'}
                </Typography>
                <Switch
                  checked={showLeveledData}
                  onChange={(e) => setShowLeveledData(e.target.checked)}
                  size="small"
                  color="primary"
                  style={{ margin: 0, padding: 0 }}
                />
              </Box>
            )}
            {isExpanded && (
              <IconButton 
                onClick={onExpand} 
                aria-label="Return to default view"
                title="Return to default view"
                size="small"
                className={classes.exitIcon}
              >
                <FullscreenExitIcon fontSize="small" />
              </IconButton>
            )}
            {!isExpanded && (
              <IconButton 
                onClick={onExpand} 
                aria-label="Expand"
                title="Expand"
                size="small"
                className={`${classes.expandIcon} ${isCollapsed ? classes.expandIconCollapsed : ''}`}
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        }
      />
      <CardContent sx={{ 
        padding: '8px 20px 20px',
        height: 'calc(100% - 45px)',
        maxHeight: 'calc(100% - 45px)',
        overflow: 'hidden',
        paddingBottom: '20px !important',
        display: isCollapsed ? 'none' : 'block',
        transition: 'opacity 0.3s ease'
      }}>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default SkipRate;
